/**
 * Small, dependency-free SVG charts for the reports page, following the
 * hand-rolled Sparkline precedent (pages/admin/home.tsx) — no chart library.
 *
 * Colors are not eyeballed: every set below was run through the dataviz
 * palette validator against this app's white card surface.
 *
 * - CHART_SERIES (categorical, assigned in fixed order, never cycled):
 *   `#0f8a67,#2a78d6,#eb6834` — the brand green snapped up into the legal
 *   lightness/chroma band, then blue and orange. Worst adjacent CVD ΔE 18.7,
 *   normal-vision ΔE 19.6, all ≥ 3:1 on white — every check passes.
 * - AGING_RAMP (ordinal, one hue light→dark for the ordered aging buckets):
 *   `#57b193,#2f9474,#127455,#0b543d` — monotone lightness, ΔL ≥ 0.06 per
 *   step, light end 2.59:1 on white, hue spread 4°.
 *
 * Marks follow the shared spec: bars ≤ 24px thick with a 4px rounded data-end
 * and a square baseline, 2px lines with round joins, ≥ 8px end markers with a
 * 2px surface ring, hairline gridlines. Values and labels wear text tokens,
 * never the series color; identity comes from the colored mark beside them.
 * Every mark carries a native <title> tooltip, and each chart's numbers are
 * also present as text (labels, tables or CSV) so nothing is color-gated.
 */

export const CHART_SERIES = ['#0f8a67', '#2a78d6', '#eb6834'] as const;
export const AGING_RAMP = ['#57b193', '#2f9474', '#127455', '#0b543d'] as const;

const INK = 'var(--color-ink)';
const MUTED = 'var(--color-muted)';
const GRID = 'var(--color-line)';
const SURFACE = '#ffffff';

/** A clean tick step (1/2/5 × 10ⁿ) so axis numbers land on round values. */
function niceStep(roughStep: number): number {
  const power = 10 ** Math.floor(Math.log10(Math.max(roughStep, 1e-9)));
  const fraction = roughStep / power;
  if (fraction <= 1) return power;
  if (fraction <= 2) return 2 * power;
  if (fraction <= 5) return 5 * power;
  return 10 * power;
}

export interface TrendSeries {
  name: string;
  color: string;
  values: number[];
}

/**
 * A weekly multi-series line chart. The legend is the dependable identity
 * channel; series are also direct-labeled at the line end, with a minimal
 * collision nudge so converging endpoints stay attached to their lines.
 */
export function WeeklyTrendChart({
  labels,
  series,
  ariaLabel,
}: {
  /** One label per week, already formatted for display. */
  labels: string[];
  series: TrendSeries[];
  ariaLabel: string;
}) {
  const width = 560;
  const height = 200;
  const pad = { top: 12, right: 84, bottom: 26, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const points = labels.length;
  if (points === 0 || series.length === 0) return null;

  const maxValue = Math.max(1, ...series.flatMap((line) => line.values));
  const step = niceStep(maxValue / 4);
  const top = Math.max(step, Math.ceil(maxValue / step) * step);
  const ticks: number[] = [];
  for (let value = 0; value <= top; value += step) ticks.push(value);

  const x = (index: number) => pad.left + (points === 1 ? plotW / 2 : (index / (points - 1)) * plotW);
  const y = (value: number) => pad.top + plotH - (value / top) * plotH;

  // Label roughly six weeks; always the first and the last.
  const labelEvery = Math.max(1, Math.ceil(points / 6));
  const xTicks = labels
    .map((label, index) => ({ label, index }))
    .filter(({ index }) => index === 0 || index === points - 1 || index % labelEvery === 0);

  // Direct end-labels: keep them at their line's end, nudged apart just enough
  // that converging series stay readable (≥ 13px vertical separation).
  const endLabels = series
    .map((line, seriesIndex) => ({ line, seriesIndex, y: y(line.values[points - 1] ?? 0) }))
    .sort((a, b) => a.y - b.y);
  for (let i = 1; i < endLabels.length; i += 1) {
    const previous = endLabels[i - 1];
    const current = endLabels[i];
    if (previous && current && current.y - previous.y < 13) current.y = previous.y + 13;
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={ariaLabel}>
      {ticks.map((value) => (
        <g key={value}>
          <line x1={pad.left} x2={pad.left + plotW} y1={y(value)} y2={y(value)} stroke={GRID} strokeWidth={1} />
          <text x={pad.left - 6} y={y(value) + 3.5} textAnchor="end" fontSize={11} fill={MUTED}>
            {value}
          </text>
        </g>
      ))}
      {xTicks.map(({ label, index }) => (
        <text key={index} x={x(index)} y={height - 8} textAnchor="middle" fontSize={11} fill={MUTED}>
          {label}
        </text>
      ))}

      {series.map((line, seriesIndex) => {
        const path = line.values
          .map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(value).toFixed(1)}`)
          .join(' ');
        const lastValue = line.values[points - 1] ?? 0;
        return (
          <g key={line.name}>
            <path
              d={path}
              fill="none"
              stroke={line.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* End marker: ≥ 8px with a 2px surface ring so it stays legible
                where lines cross. */}
            <circle cx={x(points - 1)} cy={y(lastValue)} r={4} fill={line.color} stroke={SURFACE} strokeWidth={2} />
            {/* Invisible, oversized hit targets carrying the native tooltip. */}
            {line.values.map((value, index) => (
              <circle key={index} cx={x(index)} cy={y(value)} r={9} fill="transparent">
                <title>{`${labels[index] ?? ''} — ${line.name}: ${value}`}</title>
              </circle>
            ))}
            {(() => {
              const placed = endLabels.find((entry) => entry.seriesIndex === seriesIndex);
              return (
                <text x={x(points - 1) + 9} y={(placed?.y ?? y(lastValue)) + 3.5} fontSize={11} fill={INK}>
                  {`${line.name} ${lastValue}`}
                </text>
              );
            })()}
          </g>
        );
      })}
    </svg>
  );
}

/** A bar path with the 4px rounded data-end and a square baseline. */
function barPath(barX: number, barY: number, barWidth: number, barHeight: number): string {
  const radius = Math.min(4, barWidth);
  const straight = Math.max(0, barWidth - radius);
  return [
    `M${barX},${barY}`,
    `h${straight}`,
    `a${radius},${radius} 0 0 1 ${radius},${radius}`,
    `v${Math.max(0, barHeight - radius * 2)}`,
    `a${radius},${radius} 0 0 1 ${-radius},${radius}`,
    `h${-straight}`,
    'z',
  ].join(' ');
}

export interface BarListRow {
  label: string;
  /** Bar length, on whatever scale `max` uses (money, minutes, percent). */
  value: number;
  /** The value as the reader should see it — already formatted. */
  display: string;
  color: string;
  /** A short annotation after the display value (e.g. "overbooked"). */
  note?: string;
}

/**
 * A horizontal bar list: label above each bar, the formatted value in a fixed
 * right column (a table-like reading that never collides with the bar). Every
 * value is directly labeled, so the chart needs no axis. With `showTrack` each
 * bar draws on a light track of the same green ramp — the meter form, used for
 * capacity utilization where the bar is a ratio against a limit.
 */
export function BarListChart({
  rows,
  ariaLabel,
  max,
  showTrack = false,
}: {
  rows: BarListRow[];
  ariaLabel: string;
  /** The scale's end; defaults to the largest row value. */
  max?: number;
  showTrack?: boolean;
}) {
  const width = 560;
  const rowHeight = 42;
  const barHeight = 16;
  const valueColumn = 132;
  const barAreaW = width - valueColumn - 8;
  const height = rows.length * rowHeight + 4;
  // A provided max IS the scale: capacity passes 100 so that "full" always
  // fills the track, and an overbooked 200% row clamps to the end instead of
  // re-scaling everyone else's full week to look like slack.
  const scaleMax = max ?? Math.max(...rows.map((row) => row.value), 1);

  if (rows.length === 0) return null;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={ariaLabel}>
      {rows.map((row, index) => {
        const rowTop = index * rowHeight;
        const barTop = rowTop + 20;
        const barWidth = Math.max(row.value > 0 ? 2 : 0, (Math.min(row.value, scaleMax) / scaleMax) * barAreaW);
        return (
          <g key={`${row.label}-${index}`}>
            <title>{`${row.label}: ${row.display}${row.note ? ` — ${row.note}` : ''}`}</title>
            <text x={0} y={rowTop + 13} fontSize={12} fill={INK}>
              {row.label}
            </text>
            {showTrack ? (
              <rect x={0} y={barTop} width={barAreaW} height={barHeight} rx={4} fill="var(--color-brand-soft)" />
            ) : null}
            {barWidth > 0 ? <path d={barPath(0, barTop, barWidth, barHeight)} fill={row.color} /> : null}
            <text x={width} y={barTop + barHeight - 3.5} textAnchor="end" fontSize={12} fill={INK} className="tabular">
              {row.display}
            </text>
            {row.note ? (
              <text x={width} y={rowTop + 13} textAnchor="end" fontSize={11} fill={MUTED}>
                {row.note}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/** The legend for a multi-series chart — HTML, beside the plot, text tokens. */
export function ChartLegend({ items }: { items: Array<{ name: string; color: string }> }) {
  return (
    <div className="mb-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[color:var(--color-muted)]">
      {items.map((item) => (
        <span key={item.name} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          {item.name}
        </span>
      ))}
    </div>
  );
}
